/**
 * Team Management API Service
 *
 * Handles role terminology, team assignments, and user goals.
 */

import { apiClient } from '../apiClient';
import {
  RoleTerminologyMapping,
  RoleTerminologyCreate,
} from '@/types/roleTerminology';
import {
  TeamAssignment,
  TeamAssignmentCreate,
  BulkTeamAssignment,
  TeamTransfer,
  TeamMemberWithStats,
  SupervisorTeamSummary,
  OrganizationTeamOverview,
} from '@/types/teamStructure';
import {
  UserGoal,
  UserGoalCreate,
  GoalProgressUpdate,
  BulkGoalAssignment,
  GoalTemplate,
  UserGoalSummary,
  TeamGoalSummary,
} from '@/types/userGoals';

// ============================================================================
// ROLE TERMINOLOGY API
// ============================================================================

export const roleTerminologyAPI = {
  /**
   * Get all role terminology mappings for an organization
   */
  async getTerminology(organizationId: number): Promise<RoleTerminologyMapping[]> {
    const response = await apiClient.get<RoleTerminologyMapping[]>(
      `/api/organizations/${organizationId}/role-terminology`,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Create or update role terminology
   */
  async upsertTerminology(
    organizationId: number,
    data: RoleTerminologyCreate
  ): Promise<RoleTerminologyMapping> {
    const response = await apiClient.post<RoleTerminologyMapping>(
      `/api/organizations/${organizationId}/role-terminology`,
      data,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Update specific terminology mapping by role type
   */
  async updateTerminology(
    organizationId: number,
    roleType: string,
    data: Partial<RoleTerminologyCreate>
  ): Promise<RoleTerminologyMapping> {
    const response = await apiClient.put<RoleTerminologyMapping>(
      `/api/organizations/${organizationId}/role-terminology/${roleType}`,
      data,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Delete terminology mapping (reverts to default)
   */
  async deleteTerminology(organizationId: number, roleType: string): Promise<void> {
    await apiClient.delete(
      `/api/organizations/${organizationId}/role-terminology/${roleType}`,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
  },

  /**
   * Reset all terminology to defaults
   */
  async resetToDefaults(organizationId: number): Promise<RoleTerminologyMapping[]> {
    const response = await apiClient.post<RoleTerminologyMapping[]>(
      `/api/organizations/${organizationId}/role-terminology/reset-defaults`,
      {},
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },
};

// ============================================================================
// TEAM ASSIGNMENT API
// ============================================================================

export const teamAssignmentAPI = {
  /**
   * Get organization team overview
   */
  async getTeamOverview(organizationId: number): Promise<OrganizationTeamOverview> {
    const response = await apiClient.get<OrganizationTeamOverview>(
      `/api/organizations/${organizationId}/team/overview`,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Get all team assignments for an organization
   */
  async listAssignments(
    organizationId: number,
    params?: {
      supervisor_id?: number;
      staff_id?: number;
      assignment_type?: string;
      active_only?: boolean;
    }
  ): Promise<TeamAssignment[]> {
    const response = await apiClient.get<TeamAssignment[]>(
      `/api/organizations/${organizationId}/team/assignments`,
      {
        params,
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Get supervisor's team
   */
  async getSupervisorTeam(
    organizationId: number,
    supervisorId: number
  ): Promise<SupervisorTeamSummary> {
    const response = await apiClient.get<SupervisorTeamSummary>(
      `/api/organizations/${organizationId}/team/supervisors/${supervisorId}`,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Get team members with stats for a supervisor
   */
  async getTeamMembersWithStats(
    organizationId: number,
    supervisorId: number
  ): Promise<TeamMemberWithStats[]> {
    const response = await apiClient.get<TeamMemberWithStats[]>(
      `/api/organizations/${organizationId}/team/supervisors/${supervisorId}/members`,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Create team assignment
   */
  async createAssignment(
    organizationId: number,
    data: TeamAssignmentCreate
  ): Promise<TeamAssignment> {
    const response = await apiClient.post<TeamAssignment>(
      `/api/organizations/${organizationId}/team/assignments`,
      data,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Bulk assign staff to supervisor
   */
  async bulkAssign(
    organizationId: number,
    data: BulkTeamAssignment
  ): Promise<TeamAssignment[]> {
    const response = await apiClient.post<TeamAssignment[]>(
      `/api/organizations/${organizationId}/team/assignments/bulk`,
      data,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Transfer staff to different supervisor
   */
  async transferStaff(
    organizationId: number,
    data: TeamTransfer
  ): Promise<TeamAssignment[]> {
    const response = await apiClient.post<TeamAssignment[]>(
      `/api/organizations/${organizationId}/team/transfer`,
      data,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Update assignment
   */
  async updateAssignment(
    organizationId: number,
    assignmentId: number,
    data: Partial<TeamAssignmentCreate>
  ): Promise<TeamAssignment> {
    const response = await apiClient.put<TeamAssignment>(
      `/api/organizations/${organizationId}/team/assignments/${assignmentId}`,
      data,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * End assignment (set effective_to date)
   */
  async endAssignment(
    organizationId: number,
    assignmentId: number,
    effectiveTo?: string
  ): Promise<TeamAssignment> {
    const response = await apiClient.put<TeamAssignment>(
      `/api/organizations/${organizationId}/team/assignments/${assignmentId}/end`,
      { effective_to: effectiveTo || new Date().toISOString() },
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Delete assignment
   */
  async deleteAssignment(organizationId: number, assignmentId: number): Promise<void> {
    await apiClient.delete(
      `/api/organizations/${organizationId}/team/assignments/${assignmentId}`,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
  },

  /**
   * Get unassigned staff (not assigned to any supervisor)
   */
  async getUnassignedStaff(organizationId: number): Promise<Array<{
    id: number;
    name: string;
    email: string;
    role: string;
  }>> {
    const response = await apiClient.get(
      `/api/organizations/${organizationId}/team/unassigned-staff`,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },
};

// ============================================================================
// USER GOALS API
// ============================================================================

export const userGoalsAPI = {
  /**
   * Get goals for a user
   */
  async getUserGoals(
    organizationId: number,
    userId: number,
    params?: {
      status?: string;
      goal_type?: string;
      program_id?: number;
      period_type?: string;
    }
  ): Promise<UserGoal[]> {
    const response = await apiClient.get<UserGoal[]>(
      `/api/organizations/${organizationId}/users/${userId}/goals`,
      {
        params,
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Get goal summary for a user
   */
  async getUserGoalSummary(
    organizationId: number,
    userId: number
  ): Promise<UserGoalSummary> {
    const response = await apiClient.get<UserGoalSummary>(
      `/api/organizations/${organizationId}/users/${userId}/goals/summary`,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Get team goal summary (for supervisor)
   */
  async getTeamGoalSummary(
    organizationId: number,
    supervisorId: number
  ): Promise<TeamGoalSummary> {
    const response = await apiClient.get<TeamGoalSummary>(
      `/api/organizations/${organizationId}/team/supervisors/${supervisorId}/goals/summary`,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Get all goals in organization (admin view)
   */
  async listOrganizationGoals(
    organizationId: number,
    params?: {
      user_id?: number;
      program_id?: number;
      status?: string;
      goal_type?: string;
      period_type?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{ goals: UserGoal[]; total: number }> {
    const response = await apiClient.get<{ goals: UserGoal[]; total: number }>(
      `/api/organizations/${organizationId}/goals`,
      {
        params,
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Create a goal for a user
   */
  async createGoal(
    organizationId: number,
    data: UserGoalCreate
  ): Promise<UserGoal> {
    const response = await apiClient.post<UserGoal>(
      `/api/organizations/${organizationId}/goals`,
      data,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Bulk assign goals to multiple users
   */
  async bulkAssignGoals(
    organizationId: number,
    data: BulkGoalAssignment
  ): Promise<UserGoal[]> {
    const response = await apiClient.post<UserGoal[]>(
      `/api/organizations/${organizationId}/goals/bulk`,
      data,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Update a goal
   */
  async updateGoal(
    organizationId: number,
    goalId: number,
    data: Partial<UserGoalCreate>
  ): Promise<UserGoal> {
    const response = await apiClient.put<UserGoal>(
      `/api/organizations/${organizationId}/goals/${goalId}`,
      data,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Update goal progress
   */
  async updateProgress(
    organizationId: number,
    goalId: number,
    data: GoalProgressUpdate
  ): Promise<UserGoal> {
    const response = await apiClient.put<UserGoal>(
      `/api/organizations/${organizationId}/goals/${goalId}/progress`,
      data,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Delete a goal
   */
  async deleteGoal(
    organizationId: number,
    goalId: number
  ): Promise<void> {
    await apiClient.delete(
      `/api/organizations/${organizationId}/goals/${goalId}`,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
  },

  /**
   * Get goal templates
   */
  async getTemplates(
    organizationId: number,
    goalType?: string
  ): Promise<GoalTemplate[]> {
    const response = await apiClient.get<GoalTemplate[]>(
      `/api/organizations/${organizationId}/goal-templates`,
      {
        params: goalType ? { goal_type: goalType } : undefined,
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },

  /**
   * Create goal template
   */
  async createTemplate(
    organizationId: number,
    data: Omit<GoalTemplate, 'id' | 'organization_id' | 'created_at'>
  ): Promise<GoalTemplate> {
    const response = await apiClient.post<GoalTemplate>(
      `/api/organizations/${organizationId}/goal-templates`,
      data,
      {
        headers: { 'X-CBO-ID': organizationId.toString() },
      }
    );
    return response.data;
  },
};

// Export all APIs
export default {
  terminology: roleTerminologyAPI,
  team: teamAssignmentAPI,
  goals: userGoalsAPI,
};
